import React, { useState } from 'react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import BarChart from '../../components/charts/BarChart';
import HorizontalBarChart from '../../components/charts/HorizontalBarChart';
import {
  nationalDailyGeneration,
  nationalMonthlyGeneration,
  nationalHourlyGeneration,
  nationalKPIs,
  statesData,
  top5Plants,
} from '../../data/mockData';

type Tab = 'daily' | 'monthly' | 'hourly';

export default function NationalGeneration() {
  const [tab, setTab] = useState<Tab>('daily');

  const chartData =
    tab === 'daily'   ? nationalDailyGeneration :
    tab === 'monthly' ? nationalMonthlyGeneration :
    nationalHourlyGeneration;

  const yUnit = tab === 'hourly' ? ' GW' : ' GWh';

  const stateBarData = statesData.slice(0, 7).map(s => ({
    label: s.name,
    value: s.generationGWh,
  }));

  const tabCls = (t: Tab) =>
    `px-3 py-1.5 text-xs rounded-input border transition-colors ${tab === t ? 'bg-navy text-white border-navy' : 'bg-white text-text-secondary border-border hover:border-navy hover:text-navy'}`;

  return (
    <div className="p-6 space-y-6">
      {/* KPIs — 6 cards, unit-free labels */}
      <div className="flex gap-4">
        <KPICard title="Today's Generation"    value="36.0 GWh"        subtext="+6.2% vs yesterday" subtextColor="green" />
        <KPICard title="This Month"            value="892 GWh"         subtext="Apr 2026" />
        <KPICard title="Lifetime Total"        value="4.7 TWh"         subtext="Since platform launch" />
        <KPICard title="Peak Power Today"      value="40.2 GW"         subtext="At 12:00 PM" />
        <KPICard title="Avg Specific Yield"    value={`${nationalKPIs.avgSpecificYield} kWh/kWp`} subtext="National average" />
        <KPICard title="Avg Performance Ratio" value={`${nationalKPIs.avgPR}%`} subtext="National average" subtextColor="green" />
      </div>

      {/* Main chart */}
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <div className="flex items-center justify-between mb-4">
          <SectionDivider title="Date-wise Generation" subtitle="National aggregate" />
          <div className="flex items-center gap-1.5">
            <button className={tabCls('daily')}   onClick={() => setTab('daily')}>Daily</button>
            <button className={tabCls('monthly')} onClick={() => setTab('monthly')}>Monthly</button>
            <button className={tabCls('hourly')}  onClick={() => setTab('hourly')}>Today Hourly</button>
          </div>
        </div>
        <BarChart data={chartData} yLabel={yUnit.trim()} unit={yUnit} height={280} />
      </div>

      {/* Two-column row */}
      <div className="flex gap-4">
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="State-wise Generation Today" subtitle="GWh" />
          <HorizontalBarChart
            data={stateBarData}
            unit=" GWh"
            thresholdLow={3.5}
            defaultColor="#2472C8"
            belowColor="#C25A0A"
          />
        </div>

        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Top 5 Performing Plants" subtitle="Today" />
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-text-tertiary font-medium">Plant ID</th>
                <th className="text-left py-2 text-text-tertiary font-medium">State</th>
                <th className="text-right py-2 text-text-tertiary font-medium">kWh today</th>
                <th className="text-right py-2 text-text-tertiary font-medium">PR%</th>
              </tr>
            </thead>
            <tbody>
              {top5Plants.map((p, i) => (
                <tr key={i} className="border-b border-border/50 hover:bg-bg-secondary cursor-pointer">
                  <td className="py-2.5 font-mono text-text-primary">{p.id}</td>
                  <td className="py-2.5 text-text-secondary">{p.state}</td>
                  <td className="py-2.5 text-right font-medium text-text-primary">{p.kwhToday}</td>
                  <td className="py-2.5 text-right">
                    <span className={`font-semibold ${p.pr >= 85 ? 'text-success' : 'text-warning'}`}>{p.pr}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
